import {
  ADDITIONAL_FILTERS,
  CASE_MANAGER_DROPDOWN,
  COMANAGEMENT_TEAM,
  createGroupTypeDropdown,
  DISCHARGE_REASON_DROPDOWN,
  EPISODE_TYPE_DROPDOWN,
  FilterDropdown,
  getClientFilterDropdowns,
  getComanagementTeamManagerFilterDropdowns,
  LATEST_REHAB_STATE_DROPDOWN,
  ONLY_OTHER_ORGANIZATIONS_CHECKBOX,
  PLAN_TYPE_DROPDOWN,
  sortFilterDropdowns,
  UTILIZATION_MANAGER_DROPDOWN,
} from 'constants/filterConfigs';
import { END_DATE, ONLY_OTHER_ORGANIZATIONS, OWNER_CLIENT, START_DATE } from 'constants/filterKeysConstants';
import { defaultTranslate, TranslateFnType } from 'context/translations';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';
import { InsightsFilterState } from 'stores/insightsStore';

const rehabStateDropdown = {
  ...LATEST_REHAB_STATE_DROPDOWN,
  name: 'rehabState',
};

export const getFilterSections = (
  selectedGroupType: GroupType,
  profile: Profile,
  translate: TranslateFnType = defaultTranslate
) => {
  const { caseManager: caseManagerEnabled, utilizationManager: utilizationManagerEnabled } =
    profile.actingClient.configForManagedGroupType(selectedGroupType.apiName) ?? {};

  return [
    {
      title: COMANAGEMENT_TEAM,
      filters: sortFilterDropdowns([
        createGroupTypeDropdown(selectedGroupType),
        ...getClientFilterDropdowns(selectedGroupType, profile),
        ...getComanagementTeamManagerFilterDropdowns(profile),
      ]),
    },
    {
      title: ADDITIONAL_FILTERS,
      filters: [
        // dropdown filters
        ...(caseManagerEnabled ? [CASE_MANAGER_DROPDOWN] : []),
        {
          ...DISCHARGE_REASON_DROPDOWN,
          placeholder: `${translate('Discharged')} To`,
        },
        EPISODE_TYPE_DROPDOWN,
        PLAN_TYPE_DROPDOWN,
        rehabStateDropdown,
        ...(utilizationManagerEnabled ? [UTILIZATION_MANAGER_DROPDOWN] : []),
        // checkbox filters
        ...(profile.isProviderForClientManagedAndProvidedGroupType(selectedGroupType.id)
          ? [ONLY_OTHER_ORGANIZATIONS_CHECKBOX]
          : []),
      ],
    },
  ];
};

const staticFilterKeys = (selectedGroupType: GroupType, profile: Profile) => {
  return [
    ...getComanagementTeamManagerFilterDropdowns(profile),
    ...(getFilterSections(selectedGroupType, profile).find((section) => section.title === ADDITIONAL_FILTERS)
      ?.filters || []),
  ].reduce((acc: string[], filterDropdown: FilterDropdown) => {
    return [...acc, filterDropdown.name];
  }, []);
};

const filterKeysForSelectedGroupType = (selectedGroupType: GroupType, profile: Profile) => {
  return [
    ...staticFilterKeys(selectedGroupType, profile),
    selectedGroupType?.camelCaseApiName,
    ...(profile.canActAsManager ? [`${selectedGroupType?.camelCaseApiName}Client`] : []),
    ...(profile.canActAsProvider ? [OWNER_CLIENT] : []),
    ...(profile.isProviderForClientManagedAndProvidedGroupType(selectedGroupType.id) ? [ONLY_OTHER_ORGANIZATIONS] : []),
    START_DATE,
    END_DATE,
  ];
};

export const filtersForSelectedGroupType = (
  filters: InsightsFilterState,
  selectedGroupType: GroupType,
  profile: Profile
) => {
  return Object.entries(filters).reduce(
    (acc, [key, val]) =>
      filterKeysForSelectedGroupType(selectedGroupType, profile).includes(key) ? { ...acc, [key]: val } : acc,
    {}
  );
};

export const getFiltersForSelectedGroupType = (
  filters: InsightsFilterState,
  selectedGroupType: GroupType,
  profile: Profile,
  translate: TranslateFnType = defaultTranslate
) => {
  return [
    filtersForSelectedGroupType(filters, selectedGroupType, profile),
    getFilterSections(selectedGroupType, profile, translate),
  ];
};
